/**
 * FilterBar — search input + filter dropdowns + applied filter chips + clear-all
 *
 * @example
 * <FilterBar
 *   search={search}
 *   onSearchChange={setSearch}
 *   filters={[
 *     {
 *       key: 'status',
 *       label: t('admin.col_status'),
 *       options: [
 *         { value: 'active', label: t('admin.status_active') },
 *         { value: 'inactive', label: t('admin.status_inactive') },
 *       ],
 *     },
 *   ]}
 *   activeFilters={activeFilters}
 *   onFilterChange={(key, value) => setFilter(key, value)}
 *   onClearAll={() => clearAllFilters()}
 * />
 */

import PropTypes from 'prop-types';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';

// ─── Chip ─────────────────────────────────────────────────────────────────────
function FilterChip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[var(--brand-primary)]/10 border border-[var(--brand-primary)]/30 text-[var(--brand-primary)] text-xs font-bold">
      {label}
      <button
        onClick={onRemove}
        className="hover:opacity-70 transition-opacity"
        aria-label={`Remove filter: ${label}`}
      >
        <X size={12} />
      </button>
    </span>
  );
}

// ─── FilterBar ────────────────────────────────────────────────────────────────
export default function FilterBar({
  search,
  onSearchChange,
  filters,
  activeFilters,
  onFilterChange,
  onClearAll,
  searchPlaceholder,
  className,
}) {
  const { t } = useLanguage();
  const placeholder = searchPlaceholder || t('admin.search_placeholder');

  const hasActive = activeFilters && Object.values(activeFilters).some((v) => v != null && v !== '' && v !== '__all__');

  const getChipLabel = (key, value) => {
    const filter = filters?.find((f) => f.key === key);
    if (!filter) return `${key}: ${value}`;
    const option = filter.options?.find((o) => o.value === value);
    return `${filter.label}: ${option?.label ?? value}`;
  };

  return (
    <div className={cn('space-y-2', className)}>
      {/* Search + Dropdowns row */}
      <div className="flex flex-wrap gap-2 items-center">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-[340px]">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
          />
          <Input
            value={search}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder={placeholder}
            className="pl-9 h-9 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] bg-white"
            aria-label={placeholder}
          />
          {search && (
            <button
              onClick={() => onSearchChange?.('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-main)]"
              aria-label={t('admin.clear_search')}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filter dropdowns */}
        {filters?.map((filter) => {
          const fval = activeFilters?.[filter.key] || '__all__';
          const fActive = fval !== '__all__';
          return (
            <Select
              key={filter.key}
              value={fval}
              onValueChange={(val) => onFilterChange?.(filter.key, val === '__all__' ? null : val)}
            >
              <SelectTrigger
                className={cn(
                  'h-9 w-[168px] shrink-0 rounded-xl border-2 bg-white text-sm font-semibold gap-1',
                  fActive ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]' : 'border-[var(--border-light)]'
                )}
                aria-label={filter.label}
              >
                <span className="text-[11px] font-bold text-[var(--text-muted)] shrink-0">{filter.label}</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-2 border-[var(--border-light)]">
                <SelectItem value="__all__">{t('admin.filter_all')}</SelectItem>
                {filter.options?.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        })}

        {/* Clear all */}
        {hasActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            className="h-9 rounded-xl text-[var(--text-muted)] hover:text-[var(--brand-danger)] font-semibold text-xs"
          >
            <X size={14} className="mr-1" />
            {t('admin.clear_filters')}
          </Button>
        )}
      </div>

      {/* Applied filter chips */}
      {hasActive && (
        <div className="flex flex-wrap gap-1.5" aria-label={t('admin.active_filters')}>
          {Object.entries(activeFilters).map(([key, value]) => {
            if (!value || value === '__all__') return null;
            return (
              <FilterChip
                key={key}
                label={getChipLabel(key, value)}
                onRemove={() => onFilterChange?.(key, null)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

FilterBar.propTypes = {
  search: PropTypes.string,
  onSearchChange: PropTypes.func,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        }),
      ),
    }),
  ),
  activeFilters: PropTypes.object,
  onFilterChange: PropTypes.func,
  onClearAll: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  className: PropTypes.string,
};

FilterBar.defaultProps = {
  search: '',
  filters: [],
  activeFilters: {},
};
